import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaChartBar, FaFile, FaFolderPlus } from "react-icons/fa";
import { useCreateResourceGroup, useResourceGroups } from "@/api/resource-group";
import logger from "@/utils/logger";
import { Z_INDEX } from "@/utils/z-index";
import { useDashboardNavigation } from "@/hooks/use-dashboard-navigation";
import { ContextMenuItem } from "./context-menu-helpers";

const NewDashboardButton: React.FC = () => {
  const { goToCreate, goToTemplate } = useDashboardNavigation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ bottom: number; left: number; width: number } | null>(null);

  const { data: groups = [] } = useResourceGroups("dashboard");
  const createGroupMutation = useCreateResourceGroup();

  // Handle button click - calculate position before opening
  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleCreateDashboard = () => {
    goToCreate();
    setIsOpen(false);
  };

  const handleCreateFromTemplate = () => {
    goToTemplate();
    setIsOpen(false);
  };

  const handleCreateGroup = async () => {
    try {
      await createGroupMutation.mutateAsync({
        name: "New Group",
        resource_type: "dashboard",
        order: groups.length,
      });
      // Invalidate resource groups query
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey[0];
          if (typeof queryKey === "string") {
            try {
              const config = JSON.parse(queryKey);
              return config.url?.includes("resource_group");
            } catch {
              return false;
            }
          }
          return false;
        },
      });
    } catch (error) {
      logger.error("Failed to create resource group:", error);
    }
    setIsOpen(false);
  };

  return (
    <div className="px-3 py-2">
      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleToggle}
        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed transition-all ${
          isOpen 
            ? "border-[var(--arc-accent)] text-[var(--arc-accent)]" 
            : "border-[var(--arc-sidebar-border-accent)] text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)] hover:border-[var(--arc-accent)] hover:bg-[var(--arc-sidebar-accent-glow)]"
        }`}
      >
        <FaPlus size={12} />
        <span className="text-sm">New Dashboard</span>
      </motion.button>

      {/* Dropdown Menu - rendered via portal to escape sidebar overflow */}
      {isOpen && menuPosition && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU - 1 }}
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <AnimatePresence>
            <motion.div
              key="new-dashboard-menu"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.15 }}
              style={{
                position: "fixed",
                bottom: menuPosition.bottom,
                left: menuPosition.left,
                width: menuPosition.width,
                zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU,
              }}
              className="bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] py-1"
            >
              <ContextMenuItem onClick={handleCreateDashboard}>
                <FaChartBar size={14} className="text-[var(--arc-accent)]" />
                <div className="text-left">
                  <div className="font-medium">New Dashboard</div>
                  <div className="text-xs text-[var(--arc-sidebar-text-secondary)]">Create from scratch</div>
                </div>
              </ContextMenuItem>

              <ContextMenuItem
                onClick={handleCreateGroup}
                disabled={createGroupMutation.isPending}
              >
                <FaFolderPlus size={14} className="text-[var(--arc-accent)]" />
                <div className="text-left">
                  <div className="font-medium">New Group</div>
                  <div className="text-xs text-[var(--arc-sidebar-text-secondary)]">Organize dashboards together</div>
                </div>
              </ContextMenuItem>

              <ContextMenuItem onClick={handleCreateFromTemplate}>
                <FaFile size={14} className="text-[var(--arc-accent)]" />
                <div className="text-left">
                  <div className="font-medium">From Template</div>
                  <div className="text-xs text-[var(--arc-sidebar-text-secondary)]">Use a pre-built template</div>
                </div>
              </ContextMenuItem>
            </motion.div>
          </AnimatePresence>
        </>,
        document.body
      )}
    </div>
  );
};

NewDashboardButton.displayName = "NewDashboardButton";

export default NewDashboardButton;







